import { useEffect, useMemo, useRef } from 'react';

import { useSnackbar } from 'notistack';

import dayjsService from 'services/dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoContatoStatusEnum,
  atendimentosSelector,
  fetchAtendimentosEmAndamento,
  FiltersHistoricoParams,
  resetAtendimentosEmAndamento,
} from 'store/reducers/atendimentos';

import { ModalFilterOptionsType } from './ModalFilters';
import NewListaAtendimentos from './NewListaAtendimentos';

interface Props {
  appliedFilters: ModalFilterOptionsType;
  isFilter: boolean;
}

const toApiParams = (
  filters: ModalFilterOptionsType,
): FiltersHistoricoParams => {
  const participantId =
    filters.atendente?.id || filters.participantId || undefined;

  const contatoId = filters.contato?.id || filters.contatoId;

  return {
    contatoId,
    code: filters.code,
    participantId,
    dateCreatedAtStart: filters.rangeDateCreatedAt?.[0]
      ? dayjsService(filters.rangeDateCreatedAt[0]).format('YYYY-MM-DD')
      : undefined,
    dateCreatedAtEnd: filters.rangeDateCreatedAt?.[1]
      ? dayjsService(filters.rangeDateCreatedAt[1]).format('YYYY-MM-DD')
      : undefined,
    channelCodes: filters.channelCodes?.length
      ? (filters.channelCodes as string[])
      : undefined,
    tagIds: filters.tagIds?.length ? (filters.tagIds as string[]) : undefined,
    message: filters.message || undefined,
  };
};

function TabPanelAtendimentosEmAndamento({ appliedFilters, isFilter }: Props) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const {
    atendimentosEmAndamento,
    atendimentosEmAndamentoCursor,
    loadingAtendimentosEmAndamento,
  } = useAppSelector(atendimentosSelector);

  const paginationRequestRef = useRef<{ abort: () => void }>();

  const fetchNextAtendimentos = () => {
    const { afterCursor } = atendimentosEmAndamentoCursor;
    if (!loadingAtendimentosEmAndamento && afterCursor) {
      paginationRequestRef.current = dispatch(
        fetchAtendimentosEmAndamento({
          ...toApiParams(appliedFilters),
          cursor: { afterCursor },
        }),
      );
    }
  };

  const apiParams = useMemo(
    () => toApiParams(appliedFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(appliedFilters)],
  );

  useEffect(() => {
    dispatch(resetAtendimentosEmAndamento());
    const request = dispatch(fetchAtendimentosEmAndamento(apiParams));

    request.unwrap().catch(error => {
      if (error?.name !== 'AbortError') {
        enqueueSnackbar('Falha ao carregar atendimentos');
      }
    });

    return () => {
      request.abort();
      paginationRequestRef.current?.abort();
    };
  }, [apiParams, dispatch, enqueueSnackbar]);

  useEffect(() => {
    return () => {
      dispatch(resetAtendimentosEmAndamento());
    };
  }, [dispatch]);

  return (
    <NewListaAtendimentos
      status={AtendimentoContatoStatusEnum.IN_PROGRESS}
      atendimentos={atendimentosEmAndamento}
      fetchNextAtendimentos={fetchNextAtendimentos}
      hasMore={Boolean(atendimentosEmAndamentoCursor.afterCursor)}
      isLoading={loadingAtendimentosEmAndamento}
      isFilter={isFilter}
    />
  );
}

export default TabPanelAtendimentosEmAndamento;
